/**
 * **The seam.** Everything the builder renders comes through here, and nothing else in the slice
 * knows where the document comes from.
 *
 * Execution ADR §3: in PR-A1 this hook was a query `select` running `readPlanDocument`. In PR-B1 its
 * internals became the Zustand store — the tree is loaded once, edited in place, and saved whole —
 * and **`ui/` did not change by a single file because of it**. The three files under `ui/` that PR-B1
 * touches are the deliberate edit surface (a live Save, an editable session name, a conflict
 * dialog), not the swap. That is the whole reason this file exists rather than the screen calling
 * `useQuery` itself.
 *
 * The return SHAPE is what had to stay stable, and it did: a status, a draft, whether the draft is
 * editable, a refusal, a retry. `readOnly` stopped being a constant and became a real answer; nothing
 * was reshaped.
 *
 * A lint rule holds the other half of the bargain — no file under `src/**\/ui/**` may import the
 * generated tree, type or value — so a component physically cannot reach around this hook to the
 * wire.
 *
 * ── How the query and the store divide the work ──────────────────────────────────────────────
 * The query owns FETCHING and the store owns EDITING, and the handover is one-way:
 *
 *   read → `select: readPlanDocument` → effect → `loadPlanDraft` → store → every builder surface
 *
 * Everything that produces a new tree goes back through the query cache rather than into the store
 * directly — a successful save writes the response with `setQueryData`, adopting a winner after a
 * lost race writes the winning tree the same way — so there is exactly ONE path into the document
 * and no way for the cache and the store to hold different plans. `loadPlanDraft` replaces the tree,
 * replaces the baseline and clears the undo stack, which is precisely what a load, a save and an
 * adoption each need.
 *
 * ── Why `select` and not a `useMemo` over `data` ─────────────────────────────────────────────
 * Because `readPlanDocument` MINTS slot tokens for rows that arrive blank, so running it twice on
 * the same response produces two different trees. TanStack Query memoises a `select` result on
 * `data === previousData && options.select === previousSelectFn` — verified in
 * `node_modules/@tanstack/query-core/src/queryObserver.ts`, not recalled — so passing a module-level
 * function reference makes the mapping run exactly once per load. An inline arrow would defeat that
 * on every render, which is why `readPlanDocument` is passed by name below and must never be wrapped
 * in one. With the store behind it the stakes are higher than in PR-A1: a re-minting `select` would
 * also re-fire the load effect and silently discard the trainer's edits.
 *
 * The React Compiler does not help here and must not be relied on to: it memoises within a
 * component, and this identity has to be stable across the observer's lifetime.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  releasePlanDraft,
  loadPlanDraft,
  planTemplateFullQueryOptions,
  readPlanDocument,
  usePlanDraftTree,
} from '~/entities/plan-draft';
import type { PlanDocumentRefusal, PlanDraft } from '~/entities/plan-draft';
import { ApiError } from '~/shared/api/mutator';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

/**
 * Which document the builder is about.
 *
 * A discriminated pair rather than a nullable id, and the discriminant is load-bearing: `planId`
 * has meant "the route parameter was not a usable number" since PR-A1, and that state is an ERROR.
 * A brand-new plan also has no id, and it is the opposite of an error. Folding the two would have
 * made `/plans/abc` open an empty builder instead of saying the address is wrong.
 */
export type PlanBuilderTarget =
  /**
   * `/plans/$planId`. `planId` is `null` when the parameter is not a positive integer.
   *
   * `rawId` is what the address actually said, kept because `planId` throws it away: the heading and
   * the document title fall back to `#${planId}` when the tree has not loaded, so `/plans/abc` used
   * to render a bare `#` and a tab reading `# · MoveBuddy`. A trainer with six tabs open then could
   * not tell which address had failed, and neither could a screenshot in a bug report.
   */
  | { readonly kind: 'stored'; readonly planId: number | null; readonly rawId: string }
  /** `/plans/nowy` — a seed the create modal put in the store. Nothing to fetch. */
  | { readonly kind: 'draft' };

export interface PlanDocumentState {
  /**
   * `refused` is its own state rather than a flavour of `error`, because the two need different
   * words: an error says "try again", a refusal says "this app cannot open this plan and here is
   * why". Offering a retry on a refusal would invite the trainer to keep pressing it.
   *
   * `notFound` splits off `error` for the same reason. A 404 here is almost never a lost plan — the
   * backend scopes this read to the caller, so the usual cause is a plan belonging to another
   * trainer, or a library entry this account cannot reach. None of those change on a second
   * attempt, so a retry button is a lie about what pressing it can do.
   *
   * `noDraft` is `/plans/nowy` with nothing in the store: a reload, a bookmark, a second tab. It
   * means "nothing in THIS TAB", not "gone": the screen hands the state to `ui/NewPlanNotice`,
   * which offers back the copy this device kept and otherwise says the plan is gone, never seeding
   * a blank one nobody asked for. Restoring loads the store, and this hook then answers `ready` as
   * it does for any seed.
   */
  readonly status: 'loading' | 'error' | 'notFound' | 'noDraft' | 'refused' | 'ready';
  /** The document. `null` in every state but `ready`. */
  readonly draft: PlanDraft | null;
  /**
   * Whether the trainer may edit this plan **in this build**.
   *
   * PR-A1 answered `true` unconditionally with no store behind it. It is now the honest answer to
   * the question the screen asks — "may I offer Save?" — and that answer is: yes, once the tree is
   * loaded and the store holds it.
   *
   * One case it deliberately does NOT cover, because this read cannot express it: a fork whose
   * backing assignment is `Completed` or `Cancelled` is read-only server-side (training-plan R17),
   * and `PlanTemplateFullResponse` carries no assignment status to detect it by. Such a save is
   * refused with a plain 400 carrying no code, which `saveErrorCopy`'s fallback answers with words.
   * Guessing at it from `liveLastConsumedCycle` would lock exhausted plans that are perfectly
   * editable — the exact confusion `livePlanLocks.ts` records as having been made once already.
   */
  readonly readOnly: boolean;
  /** Why the plan cannot be opened at all. Non-null exactly when `status` is `refused`. */
  readonly refusal: PlanDocumentRefusal | null;
  readonly retry: () => void;
}

export function usePlanDocument(target: PlanBuilderTarget): PlanDocumentState {
  const contentLang = useContentLanguage();

  // A brand-new draft has no id, so there is nothing to fetch and no key to fetch it under. The
  // query is still declared — hooks are not conditional — and simply never enabled.
  const planId = target.kind === 'stored' ? target.planId : null;

  const query = useQuery({
    ...planTemplateFullQueryOptions(planId ?? 0, contentLang),
    // Passed BY NAME. See the header: an inline arrow would re-mint slot tokens on every render and,
    // now, re-load the store over the trainer's edits.
    select: readPlanDocument,
    // A non-numeric `$planId` in the URL is a 404 waiting to happen, not a request worth making, and
    // `/plans/nowy` has nothing on the server at all.
    enabled: planId !== null,
  });

  const read = query.data;
  const storedDraft = usePlanDraftTree();

  // The ONE path into the document for a STORED plan. It fires on the first load and on every tree
  // the save path writes back into the cache, because both arrive as a new `select` result.
  // `loadPlanDraft` replaces the baseline and clears the undo stack, which is what each of those
  // needs. A brand-new draft has no query to arrive from — `useCreatePlanFlow` writes the store
  // directly, which is why that is documented there as the one exception rather than left implicit.
  useEffect(() => {
    if (read?.outcome === 'draft') loadPlanDraft(read.draft);
  }, [read]);

  // The store is a module singleton holding one document, so leaving the builder has to empty it —
  // otherwise the next plan opened would render the previous one's tree for a frame, and a save
  // fired before its query settled would send it.
  //
  // `releasePlanDraft`, NOT `clearPlanDraft`: an unsaved new plan exists nowhere else, and this
  // cleanup runs in situations that are not exits — `StrictMode`'s double-invocation in development
  // fires it immediately after the first mount. See the store function's own note; the symptom was
  // a freshly created plan reporting itself gone the instant `/plans/nowy` opened.
  //
  // **Keyed on the id ONLY.** It must not re-run when the target's kind changes, because the one
  // change that does that is a brand-new plan's first save turning `/plans/nowy` into
  // `/plans/<newId>`.
  useEffect(() => releasePlanDraft, [planId]);

  const retry = () => {
    void query.refetch();
  };

  if (target.kind === 'draft') {
    // The seed the create modal wrote, if it is still there. `id === null` is the whole test: a
    // stored plan's tree left over from a previous route would be the wrong document to open here.
    const seeded = storedDraft !== null && storedDraft.id === null;
    return {
      status: seeded ? 'ready' : 'noDraft',
      draft: seeded ? storedDraft : null,
      readOnly: !seeded,
      refusal: null,
      retry,
    };
  }

  if (planId === null) {
    return { status: 'error', draft: null, readOnly: true, refusal: null, retry };
  }
  if (query.isPending) {
    return { status: 'loading', draft: null, readOnly: true, refusal: null, retry };
  }
  if (query.isError || read === undefined) {
    // A 404 is terminal for this trainer, so it gets its own state and no retry. `problem.status`
    // is preferred over the transport status only because the backend sets both and the body is the
    // half that survives a proxy rewriting the response line.
    const notFound =
      query.error instanceof ApiError &&
      (query.error.problem?.status === 404 || query.error.status === 404);
    return {
      status: notFound ? 'notFound' : 'error',
      draft: null,
      readOnly: true,
      refusal: null,
      retry,
    };
  }
  if (read.outcome === 'refused') {
    return { status: 'refused', draft: null, readOnly: true, refusal: read.refusal, retry };
  }

  // The store's tree, once it holds THIS plan. Two guards, and both are load-bearing:
  //
  // · The effect above has not run yet on the render where the data first arrives, so the store is
  //   still empty — the query's own draft covers that one frame, and it is the identical object the
  //   effect is about to load, so nothing moves on screen.
  // · Navigating between two plans leaves the previous tree in the store while the next query is in
  //   flight. Matching on `id` is what stops plan 41's week rendering under plan 42's heading; the
  //   unmount effect alone cannot, because a route change re-runs this hook before it fires.
  const draft = storedDraft !== null && storedDraft.id === planId ? storedDraft : read.draft;

  return { status: 'ready', draft, readOnly: false, refusal: null, retry };
}
